import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, CreditCard, CheckCircle2, Loader2, Calendar as CalendarIcon, Clock, Users, User, Share2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import moment from 'moment';

const API = window.location.origin + '/api';

const SESSION_TYPE_CONFIG = {
  one_on_one: { label: 'One-on-One', price: 90, spots: 1, description: 'Private session with dedicated attention', icon: User },
  paired: { label: 'Paired Session', price: 150, spots: 2, description: '2 students learning together', icon: Users },
  small_group: { label: 'Small Group', price: 200, spots: 3, description: '3+ students in an interactive group', icon: Users },
};

const STEP_LABELS = ['Select Date & Time', 'Choose Session Type', 'Your Details', 'Review', 'Pay'];

const BookingFlow = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedSessionType, setSelectedSessionType] = useState(null);

  const [bookingData, setBookingData] = useState({
    num_weeks: 1,
    student_name: '',
    student_email: '',
    parent_phone: '',
    parent_name: '',
    year_level: '',
    subject: '',
    notes_to_teacher: '',
  });
  const [multiWeekAvail, setMultiWeekAvail] = useState(null);

  useEffect(() => {
    const fetchSlots = async () => {
      try {
        const response = await axios.get(`${API}/slots?include_booked=true`);
        const allSlots = response.data;
        setSlots(allSlots);

        // Handle pre-filled URL params: /book?slot=<slot_id>&type=<session_type>
        const prefillSlotId = searchParams.get('slot');
        const prefillType = searchParams.get('type');
        if (prefillSlotId) {
          const matched = allSlots.find(s => s.slot_id === prefillSlotId && s.available);
          if (matched) {
            setSelectedSlot(matched);
            setSelectedDate(new Date(matched.date + 'T00:00:00'));
            if (prefillType && SESSION_TYPE_CONFIG[prefillType]) {
              setSelectedSessionType(prefillType);
              setStep(3);
            } else {
              setStep(2);
            }
          }
        }
      } catch {
        toast.error('Error loading available times');
      }
    };
    fetchSlots();
  }, [searchParams]);

  const handleSlotSelect = (slot) => {
    if (!slot.available) {
      toast.error('This time slot is fully booked');
      return;
    }
    setSelectedSlot(slot);
    setSelectedSessionType(null);
    setBookingData(prev => ({ ...prev, num_weeks: 1 }));
    setMultiWeekAvail(null);
    setStep(2);
  };

  const handleSessionTypeSelect = (type) => {
    setSelectedSessionType(type);
    setStep(3);
  };

  const checkMultiWeek = async (numWeeks) => {
    if (numWeeks <= 1) {
      setMultiWeekAvail(null);
      setBookingData(prev => ({ ...prev, num_weeks: 1 }));
      return;
    }
    try {
      const res = await axios.get(`${API}/slots/check-multiweek?slot_id=${selectedSlot.slot_id}&num_weeks=${numWeeks}`);
      setMultiWeekAvail(res.data);
      if (res.data.all_available) {
        setBookingData(prev => ({ ...prev, num_weeks: numWeeks }));
      } else {
        toast.error('Some weeks are not available. Ask admin to add those slots first.');
        setBookingData(prev => ({ ...prev, num_weeks: 1 }));
      }
    } catch {
      toast.error('Could not check availability');
    }
  };

  const handlePayment = async () => {
    if (!bookingData.student_name || !bookingData.student_email || !bookingData.parent_phone || !bookingData.parent_name) {
      toast.error('Please fill in all required details');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/bookings`, {
        slot_id: selectedSlot.slot_id,
        session_type: selectedSessionType,
        student_name: bookingData.student_name,
        student_email: bookingData.student_email,
        parent_phone: bookingData.parent_phone,
        parent_name: bookingData.parent_name,
        num_weeks: bookingData.num_weeks,
        year_level: bookingData.year_level ? parseInt(bookingData.year_level) : null,
        subject: bookingData.subject || null,
        notes_to_teacher: bookingData.notes_to_teacher || null,
        subjects_needed: bookingData.subject || null
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
      setLoading(false);
    }
  };

  const selectedDateStr = moment(selectedDate).format('YYYY-MM-DD');
  const slotsForDate = slots.filter(s => s.date === selectedDateStr && !s.manual_booking);
  const datesWithSlots = [...new Set(slots.filter(s => s.available && !s.manual_booking).map(s => s.date))];

  const sessionPrice = selectedSessionType ? SESSION_TYPE_CONFIG[selectedSessionType].price : 0;
  const totalPrice = sessionPrice * bookingData.num_weeks;
  const typeLabel = selectedSessionType ? SESSION_TYPE_CONFIG[selectedSessionType].label : '';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <svg className="w-8 h-8 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
              <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
            </svg>
            <span className="flex flex-col leading-tight">
              <span className="text-2xl font-bold text-foreground">Igniting Minds</span>
              <span className="text-xs font-medium text-muted-foreground tracking-wide">Academic Architect</span>
            </span>
          </div>
          <Button variant="ghost" onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </div>

      {/* Progress */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="booking-wizard-progress">
          {[1, 2, 3, 4, 5].map((s) => (
            <React.Fragment key={s}>
              <div data-testid={`progress-step-${s}`} className={`step ${s === step ? 'active' : ''} ${s < step ? 'completed' : ''}`}>
                {s < step ? <CheckCircle2 className="w-5 h-5" /> : s}
              </div>
              {s < 5 && <div className="h-0.5 w-10 bg-muted" />}
            </React.Fragment>
          ))}
        </div>
        <div className="text-center text-sm text-muted-foreground mt-4">{STEP_LABELS[step - 1]}</div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 pb-12">
        <AnimatePresence mode="wait">
          {/* ===== STEP 1: SELECT DATE & TIME ===== */}
          {step === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="step-1-slot-selection">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Fraunces, serif' }}>Select Date & Time</h2>
                <p className="text-muted-foreground">Choose an available time slot — you'll select your session type next</p>
              </div>

              {/* Session Type Info */}
              <div className="flex flex-wrap justify-center gap-4 mb-8" data-testid="session-type-preview">
                {Object.entries(SESSION_TYPE_CONFIG).map(([key, cfg]) => (
                  <div key={key} className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted/60 text-sm">
                    <cfg.icon className="w-4 h-4 text-primary" />
                    <span className="font-medium">{cfg.label}</span>
                    <span className="text-primary font-bold">${cfg.price}</span>
                  </div>
                ))}
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <Card className="card-feature" data-testid="date-picker-card">
                  <div className="flex items-center gap-2 mb-4">
                    <CalendarIcon className="w-5 h-5 text-foreground" />
                    <h3 className="text-lg font-semibold" style={{ fontFamily: 'Fraunces, serif' }}>Select a Date</h3>
                  </div>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => d && setSelectedDate(d)}
                    fromDate={new Date('2026-02-02')}
                    modifiers={{ hasSlots: datesWithSlots.map(d => new Date(d + 'T00:00:00')) }}
                    modifiersStyles={{ hasSlots: { fontWeight: 700, color: 'hsl(var(--primary))' } }}
                    className="rounded-md border mx-auto"
                    data-testid="calendar-picker"
                  />
                  <p className="text-xs text-muted-foreground text-center mt-4">Timezone: Australia/Perth (AWST)</p>
                </Card>

                <Card className="card-feature" data-testid="available-times-card">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock className="w-5 h-5 text-foreground" />
                    <h3 className="text-lg font-semibold" style={{ fontFamily: 'Fraunces, serif' }}>Available Times</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-6">{moment(selectedDate).format('dddd, MMMM D, YYYY')}</p>
                  <div className="space-y-3">
                    {slotsForDate.length === 0 ? (
                      <div data-testid="no-slots-message" className="text-center py-12 text-muted-foreground">
                        <Clock className="w-10 h-10 mx-auto mb-3 opacity-40" />
                        <p className="font-medium">No slots available on this date</p>
                        <p className="text-sm mt-1">Try selecting a different date</p>
                      </div>
                    ) : (
                      slotsForDate.map(slot => (
                        <button
                          key={slot.slot_id}
                          data-testid={`slot-${slot.slot_id}`}
                          disabled={!slot.available}
                          onClick={() => slot.available && handleSlotSelect(slot)}
                          className={`w-full text-left rounded-lg border-2 p-4 transition-all ${
                            slot.available ? 'border-border hover:border-primary hover:shadow-md cursor-pointer' : 'border-muted bg-muted/30 cursor-not-allowed opacity-60'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-base font-semibold">
                                {moment(slot.start_time, 'HH:mm').format('h:mm A')} – {moment(slot.end_time, 'HH:mm').format('h:mm A')}
                              </p>
                              <p className="text-sm text-muted-foreground mt-0.5">{slot.duration} Hour</p>
                            </div>
                            <div className="text-right">
                              {slot.available ? (
                                <p className="text-sm font-medium text-green-600">Available</p>
                              ) : (
                                <p className="text-sm font-medium text-muted-foreground">Fully Booked</p>
                              )}
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </Card>
              </div>
            </motion.div>
          )}

          {/* ===== STEP 2: CHOOSE SESSION TYPE ===== */}
          {step === 2 && selectedSlot && (
            <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="step-2-session-type">
              <Button variant="ghost" onClick={() => setStep(1)} className="mb-4">
                <ChevronLeft className="w-4 h-4 mr-2" /> Back to Calendar
              </Button>

              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Fraunces, serif' }}>Choose Your Session Type</h2>
                <p className="text-muted-foreground">
                  {moment(selectedSlot.date).format('ddd, MMM D')} at {moment(selectedSlot.start_time, 'HH:mm').format('h:mm A')} – {moment(selectedSlot.end_time, 'HH:mm').format('h:mm A')}
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                {Object.entries(SESSION_TYPE_CONFIG).map(([key, cfg]) => {
                  const IconComp = cfg.icon;
                  return (
                    <button
                      key={key}
                      data-testid={`session-type-${key}`}
                      onClick={() => handleSessionTypeSelect(key)}
                      className="text-left rounded-xl border-2 p-6 transition-all border-border bg-card hover:shadow-lg hover:border-primary hover:-translate-y-1"
                    >
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                        <IconComp className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Fraunces, serif' }}>{cfg.label}</h3>
                      <p className="text-sm text-muted-foreground mb-4">{cfg.description}</p>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-primary" style={{ fontFamily: 'JetBrains Mono, monospace' }}>${cfg.price}</span>
                        <span className="text-sm text-muted-foreground">/session</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* ===== STEP 3: YOUR DETAILS ===== */}
          {step === 3 && selectedSlot && selectedSessionType && (
            <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="step-3-details-form" className="max-w-2xl mx-auto">
              <Button variant="ghost" onClick={() => setStep(2)} className="mb-4">
                <ChevronLeft className="w-4 h-4 mr-2" /> Back to Session Types
              </Button>

              <h2 className="text-3xl font-bold mb-8 text-center" style={{ fontFamily: 'Fraunces, serif' }}>Your Details</h2>

              <Card className="card-feature mb-6 bg-accent/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Selected Time</p>
                    <p className="font-semibold">{moment(selectedSlot.date).format('ddd, MMM D, YYYY')}</p>
                    <p className="text-sm">{moment(selectedSlot.start_time, 'HH:mm').format('h:mm A')} – {moment(selectedSlot.end_time, 'HH:mm').format('h:mm A')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Session</p>
                    <p className="font-semibold">{typeLabel}</p>
                    <p className="text-sm text-primary font-medium">${sessionPrice}/session</p>
                  </div>
                </div>
              </Card>

              <Card className="card-feature">
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="student_name">Student Name *</Label>
                    <Input id="student_name" data-testid="student-name-input" className="mt-2" value={bookingData.student_name} onChange={(e) => setBookingData({ ...bookingData, student_name: e.target.value })} placeholder="Enter student's full name" required />
                  </div>

                  <div>
                    <Label htmlFor="year_level">Year Level</Label>
                    <select id="year_level" data-testid="year-level-select" className="mt-2 w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm" value={bookingData.year_level} onChange={(e) => setBookingData({ ...bookingData, year_level: e.target.value })}>
                      <option value="">Select year level</option>
                      {[1,2,3,4,5,6,7,8,9,10].map(y => <option key={y} value={y}>Year {y}</option>)}
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="subject">Subject / Service</Label>
                    <select id="subject" data-testid="subject-select" className="mt-2 w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm" value={bookingData.subject} onChange={(e) => setBookingData({ ...bookingData, subject: e.target.value })}>
                      <option value="">Select subject or service</option>
                      <optgroup label="Academic Tutoring">
                        <option value="Mathematics">Mathematics</option>
                        <option value="English">English</option>
                        <option value="Science">Science</option>
                        <option value="Humanities">Humanities</option>
                        <option value="Multiple Subjects">Multiple Subjects</option>
                      </optgroup>
                      <optgroup label="Scholarship & Testing">
                        <option value="Scholarship Eligibility Testing">Scholarship Eligibility Testing</option>
                        <option value="ICAS Preparation">ICAS Preparation</option>
                        <option value="Gifted & Talented Program">Gifted & Talented Program</option>
                      </optgroup>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="notes_to_teacher">Notes to Teacher</Label>
                    <Textarea id="notes_to_teacher" data-testid="notes-to-teacher-input" className="mt-2" value={bookingData.notes_to_teacher} onChange={(e) => setBookingData({ ...bookingData, notes_to_teacher: e.target.value })} placeholder="Any specific topics, areas of concern, or goals..." rows={3} />
                  </div>

                  <div className="border-t border-border pt-6">
                    <h4 className="font-semibold mb-4">Booking Duration</h4>
                    <Label className="mb-2 block">How many weeks would you like to book?</Label>
                    <div className="grid grid-cols-5 gap-2">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(w => (
                        <button key={w} data-testid={`weeks-${w}`} onClick={() => checkMultiWeek(w)}
                          className={`rounded-lg border-2 p-3 text-center transition-all ${bookingData.num_weeks === w ? 'border-primary bg-primary/10 font-semibold' : 'border-border hover:border-primary/50'}`}
                        >
                          <p className="text-sm font-medium">{w} wk{w > 1 ? 's' : ''}</p>
                          <p className="text-xs text-muted-foreground mt-1">${sessionPrice * w}</p>
                        </button>
                      ))}
                    </div>
                    {multiWeekAvail && (
                      <div className="mt-3 space-y-1">
                        {multiWeekAvail.weeks.map((w, i) => (
                          <div key={i} className={`text-xs flex items-center gap-2 ${w.available ? 'text-green-600' : 'text-destructive'}`}>
                            <span className={`w-2 h-2 rounded-full ${w.available ? 'bg-green-500' : 'bg-destructive'}`} />
                            {moment(w.date).format('ddd, MMM D')} — {w.available ? 'Available' : 'Not available'}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="border-t border-border pt-6">
                    <h4 className="font-semibold mb-4">Parent / Guardian Details</h4>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="parent_name">Parent / Guardian Name *</Label>
                        <Input id="parent_name" data-testid="parent-name-input" className="mt-2" value={bookingData.parent_name} onChange={(e) => setBookingData({ ...bookingData, parent_name: e.target.value })} placeholder="Enter parent/guardian full name" required />
                      </div>
                      <div>
                        <Label htmlFor="student_email">Email *</Label>
                        <Input id="student_email" data-testid="student-email-input" type="email" className="mt-2" value={bookingData.student_email} onChange={(e) => setBookingData({ ...bookingData, student_email: e.target.value })} placeholder="parent@example.com" required />
                      </div>
                      <div>
                        <Label htmlFor="parent_phone">Phone Number *</Label>
                        <Input id="parent_phone" data-testid="parent-phone-input" type="tel" className="mt-2" value={bookingData.parent_phone} onChange={(e) => setBookingData({ ...bookingData, parent_phone: e.target.value })} placeholder="0400 000 000" required />
                      </div>
                    </div>
                  </div>

                  <Button data-testid="continue-to-review-btn" className="btn-primary w-full" onClick={() => setStep(4)}>Continue to Review</Button>
                </div>
              </Card>
            </motion.div>
          )}

          {/* ===== STEP 4: REVIEW ===== */}
          {step === 4 && selectedSlot && selectedSessionType && (
            <motion.div key="step4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="step-4-review" className="max-w-2xl mx-auto">
              <Button variant="ghost" onClick={() => setStep(3)} className="mb-4"><ChevronLeft className="w-4 h-4 mr-2" /> Back to Details</Button>
              <h2 className="text-3xl font-bold mb-8 text-center" style={{ fontFamily: 'Fraunces, serif' }}>Review Your Booking</h2>
              <Card className="card-feature mb-6">
                <h3 className="text-xl font-semibold mb-4">Booking Summary</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Session Type:</span><span className="font-medium">{typeLabel}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Date & Time:</span><span className="font-medium">{moment(selectedSlot.date).format('ddd, MMM D, YYYY')} at {moment(selectedSlot.start_time, 'HH:mm').format('h:mm A')}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Duration:</span><span className="font-medium">{selectedSlot.duration} hour(s)</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Price per Session:</span><span className="font-medium">${sessionPrice}</span></div>
                  {bookingData.num_weeks > 1 && <div className="flex justify-between"><span className="text-muted-foreground">Number of Weeks:</span><span className="font-medium">{bookingData.num_weeks} weeks</span></div>}
                  <div className="flex justify-between"><span className="text-muted-foreground">Student Name:</span><span className="font-medium">{bookingData.student_name}</span></div>
                  {bookingData.year_level && <div className="flex justify-between"><span className="text-muted-foreground">Year Level:</span><span className="font-medium">Year {bookingData.year_level}</span></div>}
                  {bookingData.subject && <div className="flex justify-between"><span className="text-muted-foreground">Subject:</span><span className="font-medium">{bookingData.subject}</span></div>}
                  {bookingData.notes_to_teacher && <div className="flex justify-between"><span className="text-muted-foreground">Notes:</span><span className="font-medium text-right max-w-[60%]">{bookingData.notes_to_teacher}</span></div>}
                  <div className="flex justify-between"><span className="text-muted-foreground">Parent:</span><span className="font-medium">{bookingData.parent_name}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Email:</span><span className="font-medium">{bookingData.student_email}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Phone:</span><span className="font-medium">{bookingData.parent_phone}</span></div>
                  <div className="pt-4 border-t border-border flex justify-between text-lg">
                    <span className="font-semibold">Total Amount:</span>
                    <span className="font-bold text-primary" style={{ fontFamily: 'JetBrains Mono, monospace' }}>${totalPrice.toFixed(2)} AUD</span>
                  </div>
                </div>
              </Card>
              <Button data-testid="proceed-to-payment-btn" className="btn-primary w-full text-lg py-6" onClick={() => setStep(5)}>Confirm & Proceed to Payment</Button>
              <Button
                data-testid="share-booking-link-btn"
                variant="outline"
                className="w-full mt-3"
                onClick={() => {
                  const url = `${window.location.origin}/book?slot=${selectedSlot.slot_id}&type=${selectedSessionType}`;
                  navigator.clipboard.writeText(url).then(() => toast.success('Booking link copied to clipboard!')).catch(() => toast.info(url));
                }}
              >
                <Share2 className="w-4 h-4 mr-2" /> Share This Booking Link
              </Button>
            </motion.div>
          )}

          {/* ===== STEP 5: PAY ===== */}
          {step === 5 && selectedSlot && selectedSessionType && (
            <motion.div key="step5" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="step-5-payment" className="max-w-2xl mx-auto text-center">
              <Button variant="ghost" onClick={() => setStep(4)} className="mb-4 mr-auto block text-left"><ChevronLeft className="w-4 h-4 mr-2 inline" /> Back to Review</Button>
              <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Fraunces, serif' }}>Secure Payment</h2>
              <p className="text-muted-foreground mb-8">You will be redirected to Stripe for secure payment processing.</p>
              <Card className="card-feature mb-6 text-left">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-muted-foreground">Total to pay</p>
                    <p className="text-2xl font-bold text-primary" style={{ fontFamily: 'JetBrains Mono, monospace' }}>${totalPrice.toFixed(2)} AUD</p>
                    <p className="text-xs text-muted-foreground mt-1">{typeLabel} · {bookingData.num_weeks} week{bookingData.num_weeks > 1 ? 's' : ''} · ${sessionPrice}/session</p>
                  </div>
                  <div className="text-right text-sm text-muted-foreground">
                    <p>{moment(selectedSlot.date).format('ddd, MMM D')}</p>
                    <p>{moment(selectedSlot.start_time, 'HH:mm').format('h:mm A')} – {moment(selectedSlot.end_time, 'HH:mm').format('h:mm A')}</p>
                  </div>
                </div>
              </Card>
              <Button data-testid="pay-now-btn" className="btn-primary w-full text-lg py-6" onClick={handlePayment} disabled={loading}>
                {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</> : <><CreditCard className="w-5 h-5 mr-2" /> Pay Now</>}
              </Button>
              <p className="text-center text-sm text-muted-foreground mt-4">Powered by Stripe — your payment details are never stored on our servers.</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default BookingFlow;
